import { getNumberOfDotUnit } from '@gp/utils/common';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Table } from 'tea-component';
import { groupable, injectable } from 'tea-component/lib/table/addons';
import { flattenInDevMetrics, useInDevGetColumns, IN_DEV_HEADER_GROUPS } from '../../utils';

const { autotip, pageable, scrollable, mergeable } = Table.addons;

/**
 * InDevDownload 表格组件
 * 支持多级表头、排序、背景色、分页等功能
 */
export const InDevDownloadTable = (props: any) => {
  const {
    pageSize,
    pageIndex,
    isLoading,
    handleOnPagingChange,
    showPagination = true,
    className,
    tableBodyMaxH,
    pageSizeOptions = [10, 20, 50, 100],
    triggerCfg,
    summaryData,
    timeLineData,
    sortCfg,
    total = 0,
    records,
    monthDate,
    ...restProps
  } = props || {};

  const { selectYM } = triggerCfg;
  const isMoM = selectYM?.includes('2');

  // 获取列配置和分组表头配置
  const columns = useInDevGetColumns({
    mom: isMoM,
    monthDate,
    records,
  });
  // const headerGroups = useInDevGetHeaderGroups();

  // 组合插件配置
  const addonsCfg = [{}, sortCfg || {}];

  // 是否显示分页控件
  const isShowPageCfg = total > Math.min(...pageSizeOptions);

  // 计算可见列宽总和，作为 scrollable 的 minWidth，
  // 保证表头与表体按同一固定宽度布局对齐（避免拉伸时错位）
  const tableMinWidth = useMemo(() => columns.reduce((sum: number, col: any) => sum + (col?.width || 0), 0), [columns]);

  // 过滤分组表头的 children 为当前可见列。
  // 列会随折叠状态动态增减，若分组 children 仍包含被隐藏的列，
  // groupable 会把不存在的列也按 colSpan 1 计入，导致 colSpan 虚高、表头列宽错位。
  const headerGroups = useMemo(() => {
    const visibleKeys = new Set((columns || []).map((col: any) => col?.key));
    return IN_DEV_HEADER_GROUPS.map((group: any) => ({
      ...group,
      children: (group?.children || []).filter((key: string) => visibleKeys.has(key)),
    })).filter((group: any) => group.children.length > 0);
  }, [columns]);

  // 配置表格插件
  const addons = useMemo(() => {
    const defaultAddons = [
      // 分页插件
      showPagination
        ? pageable({
          pageSizeOptions,
          recordCount: total,
          pageCountChangingResetType: 'first',
          pageSize,
          pageIndex,
          pageSizeVisible: isShowPageCfg,
          pageIndexVisible: isShowPageCfg,
          jumpVisible: isShowPageCfg,
          endJumpVisible: isShowPageCfg,
          onPagingChange: handleOnPagingChange,
          stateTextVisible: true,
          stateText: `${getNumberOfDotUnit(total)} item${total > 1 ? 's' : ''} in total`,
        })
        : [],

      // 滚动插件
      scrollable({
        minWidth: tableMinWidth,
        maxHeight: tableBodyMaxH || 'unset',
      }),
      // 自动提示插件（loading/空状态）
      autotip({
        isLoading,
        hideIcon: !isLoading,
        emptyText: 'The list has no data yet.',
        style: {
          height: 172,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box',
        },
      }),
      injectable({
        cell: (props, context: any) => {
          const columnLevel = context?.column?.level;
          const hasRecord = !!context?.record;
          return {
            className: classNames({
              // 基础容器类
              'dbs-studio-management-live-titles-table-header-text-container': true,
              // level 2 的背景色
              'dbs-table-level-2-bg': columnLevel === 2 && !hasRecord,
              'dbs-table-level-2-cell-bg': columnLevel === 2 && hasRecord,
              // level 3 的背景色
              'dbs-table-level-3-bg': columnLevel === 3 && !hasRecord,
              'dbs-table-level-3-cell-bg': columnLevel === 3 && hasRecord,
            }),
          };
        },
      }),
      ...(addonsCfg || []),
      groupable({
        headerGroups,
      }),
    ];

    return defaultAddons;
  }, [
    showPagination,
    pageSizeOptions,
    total,
    pageSize,
    pageIndex,
    isShowPageCfg,
    handleOnPagingChange,
    tableBodyMaxH,
    isLoading,
    addonsCfg,
    tableMinWidth,
    headerGroups,
  ]);

  return (
    <Table
      {...restProps}
      className={classNames(className, {
        'dbh-action-state-center-table': true,
        'dbs-common-table dbg-common-table': true,
        'dbs-indev-download-table': true,
      })}
      addons={addons}
      columns={columns}
      records={isLoading ? [] : records || []}
    />
  );
};
